<template>
  <div class="fusion-viz-pro">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex><strong>公式：</strong>²H + ³H → ⁴He + n</div>
    </div>

    <div class="main-layout vertical">
      <!-- 3D 托卡马克装置模拟 -->
      <div ref="canvasContainer" class="canvas-3d-wrapper">
         <div class="hud-center" v-if="temperature > 800">
            <div class="plasma-glow">PLASMA IGNITED</div>
         </div>
         <div class="data-hud">
            <div class="item">
               <label>约束温度</label>
               <span :style="{ color: getColor(temperature) }">{{ (temperature * 10000).toLocaleString() }} K</span>
            </div>
         </div>
      </div>

      <!-- 参数控制 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <div class="col">
                  <label>加热能量</label>
                  <el-slider v-model="temperature" :min="0" :max="1000" size="mini"></el-slider>
               </div>
               <div class="col center">
                  <el-button type="danger" size="mini" @click="doFusion" :disabled="temperature < 800">启动聚变</el-button>
               </div>
            </div>
         </div>

         <div class="theory-note">
            <p><strong>聚变原理：</strong>在极高温度下，轻核克服电斥力碰撞结合成较重核。单位质量释放的能量远超裂变，且产物无长效放射性，是人类的<strong>终极能源</strong>目标。</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'NuclearFusionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      temperature: 300,
      running: true
    }
  },
  mounted() { this.initThree(); },
  beforeDestroy() { 
    this.running = false;
    if (this.renderer) {
      this.renderer.dispose();
      this.canvasContainer.removeChild(this.renderer.domElement);
    }
  },
  methods: {
    initThree() {
      this.canvasContainer = this.$refs.canvasContainer;
      const width = this.canvasContainer.clientWidth;
      const height = this.canvasContainer.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(50, width / height, 0.1, 1000);
      this.camera.position.set(0, 50, 100);
      this.camera.lookAt(0, 0, 0);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.canvasContainer.appendChild(this.renderer.domElement);

      // Torus (Magnetically confined plasma path)
      const torusGeo = new THREE.TorusGeometry(30, 2, 32, 100);
      this.plasma = new THREE.Mesh(torusGeo, new THREE.MeshPhongMaterial({ color: 0x00d2ff, emissive: 0x00d2ff, emissiveIntensity: 1, transparent: true, opacity: 0.3 }));
      this.scene.add(this.plasma);

      // Particles (H nuclei)
      this.particles = new THREE.Group();
      for(let i=0; i<50; i++) {
          const p = new THREE.Mesh(new THREE.SphereGeometry(1.5, 8, 8), new THREE.MeshPhongMaterial({ color: 0xffffff }));
          const angle = Math.random() * Math.PI * 2;
          p.position.set(Math.cos(angle) * 30, (Math.random()-0.5)*5, Math.sin(angle) * 30);
          this.particles.add(p);
      }
      this.scene.add(this.particles);

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.4));
      this.animate();
    },
    getColor(t) {
       if (t > 800) return '#ff4757';
       if (t > 500) return '#ffd700';
       return '#00d2ff';
    },
    doFusion() {
       // Visual flash
       this.plasma.material.emissiveIntensity = 5;
       setTimeout(() => this.plasma.material.emissiveIntensity = 1, 300);
    },
    animate() {
      if (!this.running) return;
      requestAnimationFrame(this.animate);
      
      const speed = this.temperature / 1000;
      this.plasma.rotation.z += speed;
      this.plasma.material.opacity = 0.2 + (this.temperature / 1000) * 0.8;
      this.plasma.material.color.set(this.getColor(this.temperature));
      this.plasma.material.emissive.set(this.getColor(this.temperature));

      this.particles.children.forEach(p => {
          p.position.x += (Math.random()-0.5) * speed * 5;
          p.position.y += (Math.random()-0.5) * speed * 5;
          p.position.z += (Math.random()-0.5) * speed * 5;
          // Constrain back to torus
          const dist = Math.sqrt(p.position.x**2 + p.position.z**2);
          if (dist > 35 || dist < 25) {
             const angle = Math.atan2(p.position.z, p.position.x);
             p.position.x = Math.cos(angle) * 30;
             p.position.z = Math.sin(angle) * 30;
          }
      });

      this.renderer.render(this.scene, this.camera);
    }
  }
}
</script>

<style scoped lang="scss">
.fusion-viz-pro {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}
.viz-header { display: flex; justify-content: space-between; align-items: center; .title { font-size: 1rem; color: #fff; } .formula-chip { color: #ff4757; background: rgba(255, 71, 87, 0.1); padding: 4px 12px; border-radius: 8px; font-weight: bold; } }
.canvas-3d-wrapper {
  flex: 1.5; background: #000; border-radius: 12px; position: relative; overflow: hidden;
  .hud-center { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); .plasma-glow { font-size: 2rem; font-weight: 900; color: #ff4757; text-shadow: 0 0 20px #ff4757; animation: blink 0.5s infinite; } }
  .data-hud { position: absolute; top: 20px; left: 20px; .item { label { display: block; font-size: 0.6rem; color: #888; } span { font-size: 1.2rem; font-family: monospace; } } }
}
.bottom-controls { flex: 0.5; display: flex; flex-direction: column; gap: 12px; }
.control-card { background: rgba(255,255,255,0.03); border-radius: 12px; padding: 15px; .row { display: grid; grid-template-columns: 1fr 120px; gap: 20px; align-items: center; } }
.theory-note { background: rgba(255, 71, 87, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #888; line-height: 1.5; strong { color: #fff; } } }
@keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
</style>
